import { Box, Chip } from '@mui/material';
import { useIntl } from 'react-intl';

import { formatBoldText } from '@verantos-dev/feature-analytical-variables';
import { CriteriaViewCard } from '@verantos-dev/feature-cohort-builder';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import type { AnalyticalVariableCardProps } from '../types';

/**
 * Analytical-dataset variable card. A thin wrapper over the shared
 * {@link CriteriaViewCard} so dataset variables read identically to cohort
 * criteria (same serial number, type caption, title, description chrome).
 * The only dataset-specific addition is the `Default` badge, dropped into the
 * card's info-section slot via `infoBadge`.
 */
export const AnalyticalVariableCard = ({
  id,
  serialNumber,
  name,
  description,
  format,
  isDefault,
  onDelete,
  disableDelete,
  deleteTooltip,
  onClick,
  dragHandleProps,
}: AnalyticalVariableCardProps) => {
  const { formatMessage } = useIntl();

  return (
    // Preserve the `ad-variable-card-*` testid the section/e2e selectors use;
    // the inner shared card owns its own `criteria-view-card-*` testids.
    // `display: flex` passes the grid cell's stretched height down to the inner
    // card so every card in a row is the same height regardless of description
    // length (cohort renders CriteriaViewCard as the grid item directly, so it
    // stretches for free; the wrapper here would otherwise break that chain).
    <Box data-testid={`ad-variable-card-${id}`} sx={{ display: 'flex' }}>
      <CriteriaViewCard
        id={id}
        serialNumber={serialNumber}
        cardType={format.toUpperCase()}
        title={name}
        // Agent-authored descriptions carry **bold** markers — render them as
        // inline bold like the cohort criterion card does.
        details={[{ key: 'description', value: formatBoldText(description) }]}
        hideReorderButtons
        // Dataset variables have no inline edit — only delete + preview.
        canEdit={false}
        canDelete={Boolean(onDelete)}
        // While the dataset is locked the delete stays visible but disabled,
        // with the lock reason as its tooltip.
        actionsDisabled={disableDelete}
        actionTooltip={deleteTooltip}
        onDelete={onDelete}
        onClick={onClick}
        dragHandleProps={dragHandleProps}
        infoBadge={
          isDefault ? (
            <Chip
              label={formatMessage({ id: 'analyticalDatasetDetail.variables.defaultChip' })}
              size="small"
              variant="outlined"
              // Figma chip/label: 13px regular (400), full pill radius, 24px tall.
              // TODO: no VDS token for the Figma neutral fill rgba(0,0,0,0.08) —
              // needs designer input (VDS action.selected is blue-tinted); the
              // outlined border on the grey card reads close enough for now.
              sx={{
                borderRadius: `${borderRadius.full}px`,
                height: 24,
                fontWeight: 400,
              }}
              data-testid={`ad-variable-card-default-${id}`}
            />
          ) : undefined
        }
      />
    </Box>
  );
};
